import type { ExpansionAccount } from "./api";

export interface ExpansionFilters {
  q: string;
  tiers: number[];
  csm: string | null;
  /** Only accounts over their contracted allotment. */
  overOnly: boolean;
  /** Only accounts with a data-quality flag. */
  attention: boolean;
}

export type SortKey = "bill" | "over" | "name" | "tier" | "usage";

/** A row the billing team can't act on without fixing data first. */
export function needsAttention(a: ExpansionAccount): boolean {
  return (
    a.flags.no_contract ||
    a.flags.no_allotment ||
    (a.flags.no_block_price && (a.overage_contacts ?? 0) > 0)
  );
}

export function filterAccounts(
  accounts: ExpansionAccount[],
  f: ExpansionFilters,
): ExpansionAccount[] {
  const q = f.q.trim().toLowerCase();
  return accounts.filter((a) => {
    if (q && !(a.name ?? "").toLowerCase().includes(q)) return false;
    if (f.tiers.length && (a.tier == null || !f.tiers.includes(a.tier))) return false;
    if (f.csm && a.csm_token !== f.csm) return false;
    if (f.overOnly && !((a.overage_contacts ?? 0) > 0)) return false;
    if (f.attention && !needsAttention(a)) return false;
    return true;
  });
}

export function sortAccounts(
  accounts: ExpansionAccount[],
  sort: SortKey,
): ExpansionAccount[] {
  const out = [...accounts];
  const name = (a: ExpansionAccount) => (a.name ?? "").toLowerCase();
  switch (sort) {
    case "name":
      out.sort((a, b) => name(a).localeCompare(name(b)));
      break;
    case "tier":
      // T1 first; untiered last.
      out.sort((a, b) => (a.tier ?? 9) - (b.tier ?? 9) || name(a).localeCompare(name(b)));
      break;
    case "over":
      out.sort((a, b) => (b.overage_contacts ?? -1) - (a.overage_contacts ?? -1));
      break;
    case "usage":
      out.sort((a, b) => b.usage - a.usage);
      break;
    case "bill":
    default:
      // Largest gap first; rows with no computable gap sink to the bottom.
      out.sort(
        (a, b) =>
          (b.amount_to_bill ?? Number.NEGATIVE_INFINITY) -
          (a.amount_to_bill ?? Number.NEGATIVE_INFINITY),
      );
      break;
  }
  return out;
}

/** Contact counts — full precision with thousands separators ("1,234,567"). */
export function fmtContacts(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US");
}
